import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input, Button, Alert, Card, OtpInput, Badge, Logo, ORIO360_TAGLINE } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { useSidebarPrefsStore, resolveStartPath } from '@/stores/use-sidebar-prefs-store';
import { ROLE_LEVELS } from '@/types/user';
import { authService } from '@/services/auth.service';
import { ApiError } from '@/lib/api-client';

type AuthMode = 'otp-request' | 'otp-verify' | 'totp-verify';

// L06: the start page is saved per user, so it can only be read once the user is
// signed in (the auth store binds the prefs to the user) and the profile — which
// carries the role — has loaded. Read it at navigate time, never before sign-in.
function getStartPath(): string {
  const page = useSidebarPrefsStore.getState().startPage;
  const roleLevel = ROLE_LEVELS[useAuthStore.getState().profile?.role ?? ''] ?? 0;
  return resolveStartPath(page, roleLevel);
}

const CAPABILITIES = [
  { title: 'Analytics & Reports', description: 'Real-time dashboards, sales wave charts, and drill-down insights' },
  { title: 'User & Role Management', description: 'Multi-tier RBAC with location-scoped access control' },
  { title: 'Tenant Administration', description: 'Lifecycle management, approvals, and system health monitoring' },
  { title: 'Audit & Compliance', description: 'Complete audit trail, statutory tax exports, and data governance' },
];

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, profile, verifyOtp, verifyTotp } = useAuthStore();

  const [mode, setMode] = useState<AuthMode>('otp-request');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [otpSentMessage, setOtpSentMessage] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [showRecovery, setShowRecovery] = useState(false);

  // An explicit ?redirect= wins; otherwise the signed-in user's own start page.
  const explicitRedirect = searchParams.get('redirect');
  const postSignInPath = useCallback(
    () => explicitRedirect || getStartPath(),
    [explicitRedirect],
  );

  // Already signed in (or just signed in): leave once the profile is in, so the
  // start page is read for this user and role.
  useEffect(() => {
    if (isAuthenticated && profile) {
      navigate(postSignInPath(), { replace: true });
    }
  }, [isAuthenticated, profile, navigate, postSignInPath]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const clearErrors = useCallback(() => {
    setApiError('');
    setOtpSentMessage('');
  }, []);

  // Mirrors the POS login flow: a single email entry that auto-routes to
  // TOTP (for MFA-enabled users) or email OTP (everyone else).
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    clearErrors();
    setIsSubmitting(true);
    try {
      const { method } = await authService.getLoginMethod(email.trim());
      if (method === 'totp') {
        setMode('totp-verify');
      } else {
        // 'otp' (no MFA) or 'totp_setup' (MFA enforced but not yet enrolled).
        // Both paths begin with sending an email OTP — the totp_setup
        // enrollment wizard lives in the POS app.
        const result = await authService.requestOtp(email.trim());
        setOtpSentMessage(result.message);
        setMode('otp-verify');
        setResendCooldown(60);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setApiError(err.message);
      } else {
        setApiError('Failed to proceed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || otp.length !== 6) return;

    clearErrors();
    setIsSubmitting(true);
    try {
      await verifyOtp(email.trim(), otp);
      navigate(postSignInPath(), { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setApiError(err.message);
      } else {
        setApiError('OTP verification failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    clearErrors();
    try {
      const result = await authService.requestOtp(email.trim());
      setOtpSentMessage(result.message);
      setResendCooldown(60);
    } catch (err) {
      if (err instanceof ApiError) {
        setApiError(err.message);
      } else {
        setApiError('Failed to resend OTP.');
      }
    }
  };

  const handleVerifyTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    setIsSubmitting(true);
    try {
      if (showRecovery) {
        if (!recoveryCode.trim()) return;
        const data = await authService.verifyTotpRecovery(email.trim(), recoveryCode.trim());
        localStorage.setItem('bbpos_user', JSON.stringify(data.user));
        useAuthStore.setState({ user: data.user, isAuthenticated: true });
        await useAuthStore.getState().fetchProfile();
        navigate(postSignInPath(), { replace: true });
      } else {
        if (totpCode.length !== 6) return;
        await verifyTotp(email.trim(), totpCode);
        navigate(postSignInPath(), { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setApiError(err.message);
      } else {
        setApiError('Verification failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const goBack = () => {
    clearErrors();
    setOtp('');
    setTotpCode('');
    setRecoveryCode('');
    setShowRecovery(false);
    setOtpSentMessage('');
    setMode('otp-request');
  };

  return (
    <div className="flex min-h-screen">
      {/* Left Panel — Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-violet-700 via-indigo-600 to-violet-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full translate-x-1/3 -translate-y-1/3" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/4 translate-y-1/4" />
          <div className="absolute top-1/3 left-1/4 w-40 h-40 bg-white rounded-full" />
        </div>

        <div className="relative z-10 flex flex-col justify-between w-full p-12">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Logo variant="lockup" tone="white" size={36} />
              <span className="text-white/90 font-semibold text-lg">Admin</span>
            </div>
            <p className="text-sm text-violet-200">{ORIO360_TAGLINE}</p>
          </div>

          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-extrabold text-white leading-tight">
                Command Centre
                <br />
                for Your Business
              </h1>
              <p className="mt-4 text-lg text-violet-100 max-w-md">
                Monitor operations, manage users, and make data-driven decisions from a single dashboard.
              </p>
            </div>

            <div className="space-y-3">
              {CAPABILITIES.map((cap) => (
                <div
                  key={cap.title}
                  className="flex items-start gap-3 rounded-xl bg-white/10 backdrop-blur-sm p-4 border border-white/10"
                >
                  <div className="mt-0.5 w-2 h-2 rounded-full bg-violet-300 shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-white">
                      {cap.title}
                    </h3>
                    <p className="text-xs text-violet-200 leading-relaxed">
                      {cap.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-violet-300">
            &copy; {new Date().getFullYear()} Orio360. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="flex flex-1 items-center justify-center bg-neutral-50 dark:bg-neutral-900 px-6 py-12">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile-only branding */}
          <div className="lg:hidden text-center">
            <Logo variant="mark" size={48} className="inline-block mb-3" />
          </div>

          <div>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {mode === 'otp-verify' ? 'Verify OTP' : mode === 'totp-verify' ? 'Authenticator Code' : 'Admin Login'}
            </h2>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              {mode === 'otp-verify'
                ? 'Enter the code we sent to your email'
                : mode === 'totp-verify'
                ? 'Enter the 6-digit code from your authenticator app'
                : 'Sign in to the admin dashboard'}
            </p>
          </div>

          {apiError && (
            <Alert
              variant="danger"
              title="Login Failed"
              description={apiError}
              dismissible
              onDismiss={() => setApiError('')}
            />
          )}

          {otpSentMessage && !apiError && (
            <Alert
              variant="info"
              description={otpSentMessage}
              dismissible
              onDismiss={() => setOtpSentMessage('')}
            />
          )}

          <Card variant="outlined" padding="lg">
            {/* Email Entry — auto-routes to OTP or TOTP */}
            {mode === 'otp-request' && (
              <form onSubmit={handleEmailSubmit} className="space-y-5">
                <Input
                  type="email"
                  label="Email Address"
                  placeholder="admin@business.com"
                  value={email}
                  onChange={(v) => {
                    setEmail(v);
                    if (apiError) setApiError('');
                  }}
                  disabled={isSubmitting}
                  required
                  name="email"
                  size="md"
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={isSubmitting}
                  disabled={isSubmitting || !email.trim()}
                >
                  Continue
                </Button>
              </form>
            )}

            {/* OTP Verify Mode */}
            {mode === 'otp-verify' && (
              <form onSubmit={handleVerifyOtp} className="space-y-5">
                <div className="text-center">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Code sent to{' '}
                    <Badge variant="info" size="sm">{email}</Badge>
                  </p>
                </div>

                <div className="flex justify-center py-2">
                  <OtpInput
                    length={6}
                    value={otp}
                    onChange={(v) => {
                      setOtp(v);
                      if (apiError) setApiError('');
                    }}
                    disabled={isSubmitting}
                    autoFocus
                    onComplete={(fullOtp) => {
                      setOtp(fullOtp);
                    }}
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={isSubmitting}
                  disabled={isSubmitting || otp.length !== 6}
                >
                  Verify & Sign In
                </Button>

                <div className="flex items-center justify-between pt-2">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={goBack}
                    disabled={isSubmitting}
                  >
                    Back to login
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResendOtp}
                    disabled={resendCooldown > 0 || isSubmitting}
                  >
                    {resendCooldown > 0
                      ? `Resend (${resendCooldown}s)`
                      : 'Resend OTP'}
                  </Button>
                </div>
              </form>
            )}

            {/* TOTP Verify Mode */}
            {mode === 'totp-verify' && (
              <form onSubmit={handleVerifyTotp} className="space-y-5">
                {!showRecovery ? (
                  <>
                    <div className="flex justify-center py-2">
                      <OtpInput length={6} value={totpCode} onChange={(v) => { setTotpCode(v); if (apiError) setApiError(''); }} disabled={isSubmitting} autoFocus onComplete={setTotpCode} />
                    </div>
                    <Button type="submit" variant="primary" size="lg" fullWidth loading={isSubmitting} disabled={isSubmitting || totpCode.length !== 6}>
                      Verify & Sign In
                    </Button>
                  </>
                ) : (
                  <>
                    <Input
                      label="Recovery Code"
                      placeholder="AB12CD34"
                      value={recoveryCode}
                      onChange={(v) => { setRecoveryCode(v.toUpperCase()); if (apiError) setApiError(''); }}
                      disabled={isSubmitting}
                      name="recovery-code"
                      size="md"
                    />
                    <Button type="submit" variant="primary" size="lg" fullWidth loading={isSubmitting} disabled={isSubmitting || recoveryCode.length !== 8}>
                      Verify & Sign In
                    </Button>
                  </>
                )}
                <div className="flex items-center justify-between pt-2">
                  <Button variant="link" size="sm" onClick={goBack} disabled={isSubmitting}>Back to login</Button>
                  <Button variant="ghost" size="sm" onClick={() => { setShowRecovery(!showRecovery); clearErrors(); }} disabled={isSubmitting}>
                    {showRecovery ? 'Use authenticator' : 'Use recovery code'}
                  </Button>
                </div>
              </form>
            )}
          </Card>

          <p className="text-center text-xs text-neutral-400 dark:text-neutral-500 lg:hidden">
            &copy; {new Date().getFullYear()} Orio360. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
